import { memo, useCallback, useMemo } from 'react';
import { MainStackProps } from '../../types/screens';
import { FocusStateType, useFocusState } from '../../common/hooks';
import useGlobalStyles from '../../common/style_context';
import AppColors from '../../common/colors';
import { StyleSheet, Text, View } from 'react-native';
import TvPressable from '../tv_pressable';
import KeyWrapper from '../key_wrapper';
import { commonStyles } from '../../common/styles';
import { observer } from 'mobx-react-lite';
import { computed } from 'mobx';
import { VoidCb } from '../../types/others';
import { CinemaProvider } from '../../data';
import States from '../../common/states';
import localization from '../../common/localization';

type Props = MainStackProps<'ContinueWatchDialog'>;

const dialogStyles = [
  `w=30%,bg=${AppColors.containerGray},pv=12,ph=24,br=16`,
  'fs=32,fw=700,c=white,tc',
];

const ContinueWatchDialogTv = memo<Props>(
  ({ navigation, route: { params } }) => {
    const focusState = useFocusState(3);

    const [container, titleStyle] = useGlobalStyles(dialogStyles);

    const up = useCallback((keyUp?: boolean) => {
      if (keyUp) return;
      focusState.decrement();
    }, []);
    const down = useCallback((keyUp?: boolean) => {
      if (keyUp) return;
      focusState.increment();
    }, []);
    const confirm = useCallback(() => {
      const item = params.item;
      const startPosition = Math.floor(
        item.episode.length * ((item.episode.chunk % 100) / 100),
      );
      const info =
        item.episode.season !== 0
          ? {
              season: item.episode.season - 1,
              episode: item.episode.episode - 1,
            }
          : undefined;

      const f = focusState.focused;
      switch (f) {
        case 0:
          navigation.replace('PlayerTv', {
            id: item.id,
            info,
            startPosition: params.history ? startPosition : undefined,
          });
          break;
        case 1:
          if (params.history) {
            navigation.replace('PlayerTv', {
              id: item.id,
              info,
            });
          } else {
            navigation.replace('DetailsTv', { id: item.id, tab: 0 });
          }
          break;
        case 2:
          if (params.history)
            navigation.replace('DetailsTv', { id: item.id, tab: 4 });
          else {
            CinemaProvider.removeFromHistory(item.id).then(() => {
              States.contentState.updateHistory();
            });
            navigation.pop();
          }
          break;
      }
    }, [params]);
    const cancel = useCallback(() => navigation.pop(), []);

    const items = useMemo(
      () =>
        params.history ? (
          <>
            <DialogItem
              text={localization.content.continueWatching}
              index={0}
              focusState={focusState}
              onPress={confirm}
            />
            <DialogItem
              text={localization.settings.startAtBegin}
              index={1}
              focusState={focusState}
              onPress={confirm}
            />
            <DialogItem
              text={localization.settings.openDetails}
              index={2}
              focusState={focusState}
              onPress={confirm}
            />
          </>
        ) : (
          <>
            <DialogItem
              text={localization.settings.startAtBegin}
              index={0}
              focusState={focusState}
              onPress={confirm}
            />
            <DialogItem
              text={localization.settings.openDetails}
              index={1}
              focusState={focusState}
              onPress={confirm}
            />
            <DialogItem
              text={localization.settings.removeFromHistory}
              index={2}
              focusState={focusState}
              onPress={confirm}
            />
          </>
        ),
      [params.history],
    );

    return (
      <View style={styles.container}>
        <TvPressable onPress={cancel} style={commonStyles.fill} />
        <KeyWrapper
          up={up}
          down={down}
          confirm={confirm}
          cancel={cancel}
          style={container}>
          <Text style={titleStyle}>{localization.general.menu}</Text>
          {items}
        </KeyWrapper>
      </View>
    );
  },
);

type ItemProps = {
  text: string;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const itemStyles = [
  'ac,mt=16,pv=12',
  `fill,bg=${AppColors.mainBlue},br=12`,
  'fill,bg=black,br=12',
  'fs=28,fw=500,c=white',
];

const DialogItem = observer<ItemProps>(
  ({ text, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused == index).get();

    const [container, focusedContainer, unfocusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        <Text style={textStyle}>{text}</Text>
      </TvPressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ContinueWatchDialogTv;
